import { ref, reactive } from 'vue'
import { logDebug, logInfo } from '@/utils/secureLogger'

export interface ToastOptions {
  type?: 'success' | 'warning' | 'error' | 'info'
  title: string
  message: string
  duration?: number
  autoClose?: boolean
}

export interface Toast extends ToastOptions {
  id: string
}

const toasts = ref<Toast[]>([])

export function useToast() {
  const showToast = (options: ToastOptions) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    
    const toast: Toast = {
      id,
      type: 'info',
      duration: 5000,
      autoClose: true,
      ...options
    }
    
    toasts.value.push(toast)
    logDebug('Toast notification shown', { id, type: toast.type, title: toast.title })
    
    return id
  }

  const removeToast = (id: string) => {
    const index = toasts.value.findIndex(toast => toast.id === id)
    if (index > -1) {
      toasts.value.splice(index, 1)
      logDebug('Toast notification removed', { id })
    }
  }

  const clearAllToasts = () => {
    toasts.value = []
    logDebug('All toast notifications cleared')
  }

  // Convenience methods
  const showSuccess = (title: string, message: string, duration?: number) => {
    return showToast({ type: 'success', title, message, duration })
  }

  const showWarning = (title: string, message: string, duration?: number) => {
    return showToast({ type: 'warning', title, message, duration })
  }

  const showError = (title: string, message: string, duration?: number) => {
    return showToast({ type: 'error', title, message, duration })
  }

  const showInfo = (title: string, message: string, duration?: number) => {
    return showToast({ type: 'info', title, message, duration })
  }

  return {
    toasts: toasts.value,
    showToast,
    removeToast,
    clearAllToasts,
    showSuccess,
    showWarning,
    showError,
    showInfo
  }
}